"use client";

import React, { useState, useRef, useEffect } from "react";
import { defaultTheme } from "@/src/config/colors";
import { ChevronDown, ChevronUp } from "lucide-react";

interface SelectProps {
  label?: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  error?: string;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current active selection object
  const currentOption = options.find((opt) => opt.value === value);
  const currentLabel = currentOption ? currentOption.label : "";

  // Dynamic label text: Strips out repetitive "Sort:" prefixes if an external label exists
  const displayLabel = 
    label && currentLabel.toLowerCase().startsWith("sort:") 
      ? currentLabel.substring(5).trim() 
      : currentLabel;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectOption = (optionValue: string) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  return (
    /* Changed flex-col to flex-row and items-center to sit inline */
    <div ref={dropdownRef} className="inline-flex flex-row items-center gap-2 relative max-w-full">
      {label && (
        /* Added shrink-0 to prevent label wrapping text */
        <label className="text-sm font-medium shrink-0" style={{ color: defaultTheme.text.primary }}>
          {label}
        </label>
      )}

      <div className="relative inline-flex w-auto">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-auto min-w-[140px] pl-3.5 pr-9 py-2.5 text-sm border rounded-[10px] text-left transition-colors duration-200 focus:outline-none cursor-pointer font-medium flex items-center justify-between gap-2 ${
            error ? "border-red-500" : "border-gray-300"
          } ${className}`}
          style={{
            borderColor: error ? "#EF4444" : defaultTheme.border,
            color: defaultTheme.text.primary,
            background: "var(--surface)",
          }}
        >
          {/* Renders cleaned item text cleanly */}
          <span className="truncate">{displayLabel}</span>
        </button>

        <div
          className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center"
          style={{ color: defaultTheme.text.primary }}
        >
          {isOpen ? (
            <ChevronUp className="h-4 w-4 stroke-[2.5]" />
          ) : (
            <ChevronDown className="h-4 w-4 stroke-[2.5]" />
          )}
        </div>

        {isOpen && (
          <div
            className="absolute top-full left-0 mt-1 w-full min-w-[160px] rounded-[10px] border shadow-lg z-50 overflow-hidden py-1 flex flex-col"
            style={{
              background: "var(--surface)",
              borderColor: defaultTheme.border,
            }}
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelectOption(option.value)}
                  className="w-full text-left px-3.5 py-2 text-sm font-medium transition-colors duration-150 flex items-center gap-2 hover:bg-black/5"
                  style={{
                    color: isSelected ? defaultTheme.text.primary : "var(--techno-black-70)",
                    backgroundColor: isSelected ? "rgba(0, 0, 0, 0.03)" : "transparent",
                  }}
                >
                  {isSelected && <span className="text-xs">✓</span>}
                  <span className={isSelected ? "font-semibold" : "font-medium"}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && <span className="text-xs text-red-500 absolute top-full left-0 mt-1">{error}</span>}
    </div>
  );
};
